import {Injectable} from "@angular/core";
import {IDatasource, IGetRowsParams} from "ag-grid-community";
import {DxGridDatasource} from "../domain/providers/dx-grid-datasource";
import {AgGridUtils} from "../domain/ag-grid/ag-grid-utils";
import {DxGridOptions} from "../domain/ui-options/dx-grid-options";
import {Observable, Subject} from "rxjs";
import {DxAppError} from "../../dx-core/domain/error/dx-app-error";
import {DxHttpDispatcher} from "../../dx-core/services/dx-http-dispatcher.service";
import {DxGridRow} from "../domain/dx-grid-row";
import {DxNciDictionary} from "../../dx-nci-core/domain/dx-nci-dictionary";
import {DxNciService} from "../../dx-nci-core/services/dx-nci.service";
import {map} from "rxjs/operators";


/** Внутренний датасорс AgGrid, используемый для работы серверной модели данных. */
@Injectable()
export class AgGridDatasource implements IDatasource {
  /**
   * Ссылка на прикладной датасорс.
   * @name _dxGridDS
   * @type {DxGridDatasource}
   */
  private _dxGridDS: DxGridDatasource;

  /**
   * Ссылка на датасорс для справочников NCI.
   * @name _dxGridDictsDS
   * @type {DxNciService}
   */
  private _dxGridDictsDS: DxNciService;

  /**
   * Сылка на опции грида-обертки над AgGrid.
   * @name _dxGridOptions
   * @type {DxGridOptions}
   */
  private _dxGridOptions: DxGridOptions;

  /**
   * Обсервэбл-объект для наблюдения за началом \ окончанием загрузки данных.
   * Чтобы не тащить управление визуальным состоянием грида в сервис.
   * `true` - загрузка пошла. показываем лоадер
   * `false` - загрузка завершена или завершилась ошибкой.
   * @name _loadObservable
   * @type {Subject<boolean>}
   */
  private readonly _loadObservable: Subject<boolean>;

  /**
   * Обсервэбл-объект для наблюдения за началом \ окончанием загрузки данных.
   * Чтобы не тащить управление визуальным состоянием грида в сервис.
   * `true` - загрузка пошла. показываем лоадер
   * `false` - загрузка завершена или завершилась ошибкой.
   * @name _loadErrorObservable
   * @type {Subject<DxAppError>}
   */
  private readonly _loadErrorObservable: Subject<DxAppError>;

  /**
   * Обсервэбл-объект на результат загрузки справочников НСИ для грида.
   * @name _dictsDataObservable
   * @type {Subject<Map<string, DxNciDictionary>>>}
   */
  private readonly _dictsDataObservable: Subject<Map<string, DxNciDictionary>>;

  /**
   * Флаг устанавливающий необходимость загрузки справочников при первичной загрузку данных и рефреше(???).
   */
  private _loadDicts: boolean = false;

  /** Количество записей. */
  public rowCount: number;

  /**
   * Чисто для эмуляции серверного удаления!!!
   * Проблема в том, что модель данных с бесконечным скролом не поддерживает удаление строк!
   * Требуется полный рефреш кэша :((
   *
   * Поэтому для эмуляции удаленя на период жизни компонента будем заполнять массив строк.
   * Удаленная строка навсегда исключается из выборки.
   */
  private _removedRows: DxGridRow[];

  /**
   * Внимание!!!
   * Чисто для клиентской эмуляции фильтрации грида по колнкам с типом справочник!!!
   * Удалить при реализации бэка!!!
   */
  private _gridDicts: Map<string, DxNciDictionary>;

  /**
   * Загрузка данных может происходить в два этапа: загрузка справочников и собственно записей.
   * В этом промежутке вью, в котором хостится грид могут закрыть и начнется разрушение.
   * Но асинхронный колбэк при этом все равно отработает и получится ошибка.
   */
  private _isAlive: boolean = true;


  constructor() {
    this._loadObservable = new Subject<boolean>();
    this._loadErrorObservable = new Subject<DxAppError>();
    this._dictsDataObservable = new Subject<Map<string, DxNciDictionary>>();
    this._removedRows = [];
    this._isAlive = true;
  }

  set loadDicts(value: boolean) {
    this._loadDicts = value;
  }

  set dxGridDS(value: DxGridDatasource) {
    this._dxGridDS = value;
  }

  set dxGridDictsDS(value: DxNciService) {
    this._dxGridDictsDS = value;
  }

  set dxGridOptions(value: DxGridOptions) {
    this._dxGridOptions = value;
  }

  get loadObservable(): Subject<boolean> {
    return this._loadObservable;
  }

  get loadErrorObservable(): Subject<DxAppError> {
    return this._loadErrorObservable;
  }

  get dictsDataObservable(): Subject<Map<string, DxNciDictionary>> {
    return this._dictsDataObservable;
  }

  public addRemovedRows(rows: DxGridRow[]): void {
    rows.forEach(row => this._removedRows.push(row));
  }

  /** Высвобождает задействованные ресурсы. */
  public destroy(): void {
    this._dxGridDS = null;
    this._dxGridOptions = null;
    this.rowCount = 0;
    this._removedRows.length = 0;
    this._gridDicts = null;
    this._isAlive = false;
  }


  /**
   * Фэтчит данные для грида на основании параметров через прикладной датасорс.
   * @param {IGetRowsParams} params - параметры фэтча данных комопнета AgGrid: startRow, endRow, sortModel, filterModel.
   */
  public getRows(params: IGetRowsParams): void {
    if(this._isAlive) {
      if(this._loadDicts) {
        this._loadObservable.next(true);
        this._fetchDicts().subscribe(dictsMap => {
          this._loadDicts = false;
          // TODO: Удалить строку ниже при реализации бэка!!
          // TODO: Это делается только для эмуляции серверной фильтрации
          this._gridDicts = dictsMap;
          this._dictsDataObservable.next(dictsMap);
          if(this._isAlive) this._fetchPage(params);
        }, error => {
          this._loadObservable.next(false);
          this._loadErrorObservable.next(DxHttpDispatcher.handleHttpError(error));
          params.failCallback();
        })
      } else {
        if(this._isAlive) this._fetchPage(params);
      }
    } else {
      params.successCallback([], 0);
    }
  }


  private _fetchDicts(): Observable<Map<string, DxNciDictionary>> {
    const idents: string[] = [];
    this._dxGridOptions.columns
      .filter(column => column.dataType.isDictionary)
      .forEach(column => idents.push(column.dataType.dictIdent));

    return this._dxGridDictsDS.fetchDictionaries(idents).pipe(map((dictionaries: DxNciDictionary[]) => {
      const dictsMap: Map<string, DxNciDictionary> = new Map();
      dictionaries.forEach(dict => dictsMap.set(dict.ident, dict));
      return dictsMap;
    }));
  }


  private _fetchPage(params: IGetRowsParams): void {
    const pageSize = params.endRow - params.startRow;
    const pageIndex = params.endRow / pageSize - 1;
    const page = this._dxGridDS.getNewPage(pageIndex, pageSize);

    let sorts;
    if(params.sortModel) {
      sorts = AgGridUtils.prepareSorts(this._dxGridOptions.columns, params.sortModel);
    }

    let filters;
    if(params.filterModel) {
      filters = AgGridUtils.prepareFilters(this._dxGridOptions.columns, params.filterModel, this._gridDicts);
    }

    this._loadObservable.next(true);
    this._dxGridDS.fetchPage(page, filters, sorts, this._dxGridOptions).subscribe(page => {
      this.rowCount = page.getPageData().length;
      // ВНИМАНИЕ!!!
      // lastRow - является ограничением, останавливающим фэтч данных.
      // Ей должно всегда устанавливаться общее количество записей, попавших под условия фильтрации.
      const lastRow = page.getTotalRowsCount();
      this._loadObservable.next(false);
      // TODO: Чисто для клиентской эмуляции серверного удалени!!!
      // TODO: Удалить это при реализации удаления на сервере
      const data = page.getPageData().filter(row => !this._isRemoved(row));
      params.successCallback(data, lastRow);
    }, error => {
      this._loadObservable.next(false);
      this._loadErrorObservable.next(DxHttpDispatcher.handleHttpError(error));
      params.failCallback();
    });
  }


  /**
   * Метод нужен только для клиентской эмуляции серверного удаления!!!
   * Убрать его после реализации серверного удаления.
   */
  private _isRemoved(row: DxGridRow): boolean {
    let removedRow: DxGridRow;
    for(let i = 0; i < this._removedRows.length; i++) {
      removedRow = this._removedRows[i];
      if(removedRow.getRowId() === row.getRowId()) return true;
    }
    return false;
  }
}
